import { type Attachment, MESSAGE_STATUS } from '@zg-rentals/satellite-components';
import {
  type SatelliteConversationMessageType,
  SatelliteUiTreatmentType,
  type SatelliteWebLandlordConversationAddMessageResponseDto,
  type SatelliteWebLandlordConversationGetMessagesResponseDto,
  type SatelliteWebLandlordGetMessagesActiveParticipantDto,
  type SatelliteWebLandlordGetMessagesAttachmentDto,
  type SatelliteWebLandlordGetMessagesListingDetailsDto,
  type SatelliteWebLandlordGetMessagesMessageDto,
  type SatelliteWebLandlordGetMessagesNoteDto,
  type SatelliteWebLandlordGetMessagesQuickResponseDto,
  type SatelliteWebLandlordGetMessagesRenterProfileDto,
  type SatelliteWebLandlordGetMessagesStatusDto,
  type SatelliteWebLandlordGetMessagesStatusLabelDto,
} from '@zg-rentals/ts-satellite-client';

/**
 * Attachment shape used by MessageList consumers — mirrors the legacy
 * `ZRM_Attachment` GraphQL shape (with `zDocId` casing) so downstream UI
 * components can keep their existing field reads after the Proxit migration.
 *
 * Mapped from `SatelliteWebLandlordGetMessagesAttachmentDto` (which uses
 * lowercase `zdocId`).
 */
export type ConversationMessageAttachment = {
  attachmentMetadataStatus: string;
  downloadUrl?: string;
  fileName: string;
  fileSizeBytes: number;
  fileType: string;
  isAttachmentDeleted: boolean;
  isAttachmentExpired: boolean;
  previewUrl?: string;
  zDocId?: string;
};

/** Client-side reason a send failed. Optimistic-only — never returned by the backend. */
export enum MessageSendFailureReason {
  MessageTooLong = 'messageTooLong',
}

/**
 * Client-only `messageType` for the fabricated "failed to load" message item
 * (MessageListItems) — not a real Proxit message type.
 */
export const FAILED_TO_LOAD_MESSAGE_TYPE = 'FAILED_TO_LOAD';

type OptimisticSendStatus = typeof MESSAGE_STATUS.SENDING | typeof MESSAGE_STATUS.FAILED;

/**
 * `uiTreatmentType` as stored in the messages cache: the raw Proxit enum, or the
 * optimistic `-sending` / `-failed` variant AddMessage writes while a send is in flight.
 * The optimistic suffix only ever attaches to `LOW_PRIORITY` (see SENDING/FAILED_UI_TREATMENT).
 */
export type UiTreatmentTypeWithSendStatus =
  | SatelliteUiTreatmentType
  | `${SatelliteUiTreatmentType.LOW_PRIORITY}-${OptimisticSendStatus}`;

export const SENDING_UI_TREATMENT = `${SatelliteUiTreatmentType.LOW_PRIORITY}-${MESSAGE_STATUS.SENDING}` as const;
export const FAILED_UI_TREATMENT = `${SatelliteUiTreatmentType.LOW_PRIORITY}-${MESSAGE_STATUS.FAILED}` as const;

/**
 * Message shape used by MessageList consumers and cache writers.
 *
 * Differences from the raw `SatelliteWebLandlordGetMessagesMessageDto`:
 *  - `messageDateMs` is a `string` here (DTO returns `number`) so existing
 *    consumers that pass it through to string-typed display components keep
 *    working without conversion at every call site.
 *  - `attachments[].zDocId` (camelCase D) — the DTO uses lowercase `zdocId`.
 *  - `messagePreview` is optional and not returned by the new `getMessages`
 *    endpoint; it exists on this type only so optimistic cache writes
 *    (AddMessage, ListingMessage) can still attach it when constructing temp
 *    messages.
 */
export type ConversationMessage = {
  /** Stored as the raw wire string value (e.g. `'ELISE_AI'`). Both the Proxit
   *  `SatelliteAiAutoResponseType` enum (from `@zg-rentals/ts-satellite-client`)
   *  and the GraphQL-generated enum share these wire values, so consumers can
   *  compare against either. `null` when there's no auto-response. */
  aiAutoResponseType: string | null;
  attachments: Array<ConversationMessageAttachment>;
  conversationId: string;
  isMessageOwner: boolean;
  message: string;
  messageDateMs: string;
  messageId: string;
  messageLink?: { text: string; url: string };
  /** Optimistic-only — not returned by `getMessages`. Set client-side when adding/resending. */
  messagePreview?: string;
  /** Raw Proxit enum, plus the client-only "failed to load" sentinel. */
  messageType: SatelliteConversationMessageType | typeof FAILED_TO_LOAD_MESSAGE_TYPE;
  senderName?: string;
  /** Optimistic-only — set when a send fails so the failed bubble can adapt (e.g. suppress retry). */
  sendFailureReason?: MessageSendFailureReason;
  /** Bold heading shown above the body for system messages (tours, applications, leases).
   *  Server-formatted by the backend (e.g. "Tour booked: Wed, Oct 29 at 8am"). */
  title?: string;
  uiTreatmentType: UiTreatmentTypeWithSendStatus;
};

/**
 * Mapped representation of the `getMessages` Proxit response that mirrors the
 * legacy `MessageList_GetConversationQuery['ZRM_getConversationById']` shape
 * the existing message-list consumers and cache writers expect.
 */
export type ConversationMessagesData = {
  conversationId: string;
  conversation: Array<ConversationMessage>;
  hasUnreadMessage: boolean;
  hasQuickResponses: boolean;
  quickResponses?: SatelliteWebLandlordGetMessagesQuickResponseDto;
  isConversationBlockedByZillow: boolean;
  note: SatelliteWebLandlordGetMessagesNoteDto;
  renterProfile: SatelliteWebLandlordGetMessagesRenterProfileDto;
  renterName?: string;
  renterEmail?: string;
  renterPhone?: string;
  status: SatelliteWebLandlordGetMessagesStatusDto;
  statusLabel: SatelliteWebLandlordGetMessagesStatusLabelDto;
  isPhoneLead: boolean;
  activeParticipants: Array<SatelliteWebLandlordGetMessagesActiveParticipantDto>;
  isApplicationsAllowed: boolean;
  isApplicationsAccepted: boolean;
  isApplicationCompleted: boolean;
  listingDetails: SatelliteWebLandlordGetMessagesListingDetailsDto;
  pagination: {
    pageConversationCount: number;
    queryFullMessageCount: number;
  };
};

const mapApiAttachmentToConversationAttachment = (
  attachment: SatelliteWebLandlordGetMessagesAttachmentDto,
): ConversationMessageAttachment => ({
  attachmentMetadataStatus: attachment.attachmentMetadataStatus,
  downloadUrl: attachment.downloadUrl,
  fileName: attachment.fileName,
  fileSizeBytes: attachment.fileSizeBytes,
  fileType: attachment.fileType,
  isAttachmentDeleted: attachment.isAttachmentDeleted,
  isAttachmentExpired: attachment.isAttachmentExpired,
  previewUrl: attachment.previewUrl,
  zDocId: attachment.zdocId,
});

/**
 * `String(undefined)` is `'undefined'` — truthy, and `NaN` once coerced — so a missing wire value
 * would slip past every `messageDateMs && …` guard downstream and reach date-fns, which throws on
 * an invalid date. Collapse nullish to `''` so those guards behave as written.
 */
const toTimestampString = (messageDateMs: number | null | undefined): string =>
  messageDateMs == null ? '' : String(messageDateMs);

/**
 * True when a `messageDateMs` string is a real epoch we can hand to date-fns, which throws
 * `RangeError` on an invalid date. Checking the constructed `Date` rather than just the number
 * also rejects values past the ±8.64e15 range JS `Date` supports — those are finite and positive
 * but still produce an Invalid Date.
 */
export const isDisplayableTimestamp = (messageDateMs: string): boolean => {
  const timestamp = Number(messageDateMs);
  return timestamp > 0 && !Number.isNaN(new Date(timestamp).getTime());
};

export const mapApiMessageToConversationMessage = (
  apiMessage: SatelliteWebLandlordGetMessagesMessageDto,
): ConversationMessage => ({
  aiAutoResponseType: apiMessage.aiAutoResponseType,
  attachments: apiMessage.attachments.map(mapApiAttachmentToConversationAttachment),
  conversationId: apiMessage.conversationId,
  isMessageOwner: apiMessage.isMessageOwner,
  message: apiMessage.message,
  messageDateMs: toTimestampString(apiMessage.messageDateMs),
  messageId: apiMessage.messageId,
  messageLink: apiMessage.messageLink,
  messageType: apiMessage.messageType,
  senderName: apiMessage.senderName,
  title: apiMessage.title,
  uiTreatmentType: apiMessage.uiTreatmentType,
});

export const mapGetMessagesResponseToConversationData = (
  response: SatelliteWebLandlordConversationGetMessagesResponseDto,
): ConversationMessagesData => {
  const { conversation, pageMessageCount, queryFullMessageCount } = response;
  return {
    conversationId: conversation.conversationId,
    conversation: conversation.messages.map(mapApiMessageToConversationMessage),
    hasUnreadMessage: conversation.hasUnreadMessage,
    hasQuickResponses: conversation.hasQuickResponses,
    quickResponses: conversation.quickResponses,
    isConversationBlockedByZillow: conversation.isConversationBlockedByZillow,
    note: conversation.note,
    renterProfile: conversation.renterProfile,
    renterName: conversation.renterName,
    renterEmail: conversation.renterEmail,
    renterPhone: conversation.renterPhone,
    status: conversation.status,
    statusLabel: conversation.statusLabel,
    isPhoneLead: conversation.isPhoneLead,
    activeParticipants: conversation.activeParticipants,
    isApplicationsAllowed: conversation.isApplicationsAllowed,
    isApplicationsAccepted: conversation.isApplicationsAccepted,
    isApplicationCompleted: conversation.isApplicationCompleted,
    listingDetails: conversation.listingDetails,
    pagination: {
      pageConversationCount: pageMessageCount,
      queryFullMessageCount,
    },
  };
};

/**
 * Flag the given attachments on one message as deleted. Shared by the React Query
 * cache patch (latest page) and MessageListItems' "Load more" pages — those live in
 * component state, so a cache write alone can't reach them.
 */
export const markMessageAttachmentsDeleted = (
  messages: Array<ConversationMessage>,
  messageId: string,
  zdocIds: Array<string>,
): Array<ConversationMessage> => {
  const deletedIds = new Set(zdocIds);
  return messages.map((message) => {
    if (message.messageId !== messageId) return message;
    return {
      ...message,
      attachments: message.attachments.map((attachment) =>
        attachment.zDocId && deletedIds.has(attachment.zDocId)
          ? { ...attachment, isAttachmentDeleted: true }
          : attachment,
      ),
    };
  });
};

/**
 * Fields the optimistic cache write supplies on top of the Proxit `addMessage` response.
 * `attachments` and `messagePreview` aren't returned by the endpoint — the sender already
 * holds them locally — so the caller passes them through to the inserted message.
 */
export type AddMessageResponseOverrides = {
  attachments?: Array<ConversationMessageAttachment>;
  messagePreview?: string;
};

/**
 * Normalize the loosely-typed `Attachment` (nullable fields, used by the message composer and
 * rendered messages) into the strict `ConversationMessageAttachment` shape stored in the messages
 * cache. Shared by the AddMessage send path and the ListingMessage resend path so both write
 * attachments back into the cache identically.
 */
export const toConversationMessageAttachments = (
  attachments: Array<Attachment>,
): Array<ConversationMessageAttachment> =>
  attachments.map((a) => ({
    attachmentMetadataStatus: a.attachmentMetadataStatus ?? 'complete',
    downloadUrl: a.downloadUrl ?? undefined,
    fileName: a.fileName ?? '',
    fileSizeBytes: a.fileSizeBytes ?? 0,
    fileType: a.fileType ?? '',
    isAttachmentDeleted: a.isAttachmentDeleted ?? false,
    isAttachmentExpired: a.isAttachmentExpired ?? false,
    previewUrl: a.previewUrl ?? undefined,
    zDocId: a.zDocId ?? undefined,
  }));

/**
 * Normalize a Proxit `addMessage` response into the `ConversationMessage` shape stored in the
 * React Query messages cache. Mirrors `mapApiMessageToConversationMessage` (the read path) so
 * the optimistically-inserted message renders identically to the one the next `getMessages`
 * poll returns. `attachments`/`messagePreview` aren't part of the response and are supplied
 * by the caller from the just-sent local data.
 */
export const mapAddMessageResponseToConversationMessage = (
  response: SatelliteWebLandlordConversationAddMessageResponseDto,
  overrides: AddMessageResponseOverrides = {},
): ConversationMessage => ({
  aiAutoResponseType: null,
  attachments: overrides.attachments ?? [],
  conversationId: response.conversationId,
  isMessageOwner: true,
  message: response.message ?? '',
  messageDateMs: toTimestampString(response.messageDateMs),
  messageId: response.messageId,
  messageLink: undefined,
  messagePreview: overrides.messagePreview,
  messageType: response.messageType,
  senderName: response.senderName ?? '',
  title: undefined,
  uiTreatmentType: response.uiTreatmentType,
});

/**
 * Build the React Query messages-cache update for a successful resend (the
 * resend mutation's success handler in ListingMessage).
 *
 * Behavior:
 *  - Drop the failed message we were resending (matched by `failedMessageId`).
 *    Note its id still starts with `temp-` because `AddMessage`'s onError only
 *    flips `uiTreatmentType` to the failed treatment, so the loop below would
 *    otherwise pull it into `tempMessages` and re-append it at the tail,
 *    leaving the stale "failed" bubble alongside the real sent message.
 *  - Pull other in-flight `temp-` messages (any concurrent sends) aside so we
 *    can re-append them after the real message.
 *  - Drop any pre-existing copy of the real messageId — a websocket-driven
 *    refetch or 60s poll can land the real message in cache before the resend
 *    resolves; without normalization, we'd insert a duplicate.
 *  - Append the real server message, then the preserved in-flight temps.
 *
 * `overrides` carries the attachments/preview the resend already holds locally — the Proxit
 * `addMessage` response doesn't echo them back, so the caller supplies them (mirroring the
 * AddMessage send path).
 *
 * Exported as a pure function so the reducer is unit-testable without rendering
 * the ListingMessage component or a real QueryClient.
 */
export function buildResendCacheUpdate(
  existing: ConversationMessagesData,
  newMessage: SatelliteWebLandlordConversationAddMessageResponseDto,
  failedMessageId: string,
  overrides: AddMessageResponseOverrides = {},
): ConversationMessagesData {
  const tempMessages: Array<ConversationMessage> = [];
  const realMessageId = newMessage.messageId;
  const sentMessages = existing.conversation.filter((message) => {
    if (message.messageId === failedMessageId) return false;
    const isTemp = message.messageId?.includes('temp');
    if (isTemp) {
      tempMessages.push(message);
      return false;
    }
    return true;
  });
  const cleanedSentMessages = sentMessages.filter((m) => m.messageId !== realMessageId);
  const insertedMessage = mapAddMessageResponseToConversationMessage(newMessage, overrides);
  return {
    ...existing,
    conversation: [...cleanedSentMessages, insertedMessage, ...tempMessages],
  };
}
